import React, { useState, forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiActivity, FiCalendar, FiLock, FiUser } from 'react-icons/fi';
import dayjs from 'dayjs';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';

import { Label } from '../common/Label';
import { Input } from '../common/Input';
import { DateInput } from '../common/DateInput';
import { FormField as FormFieldCommon } from '../common/FormField';
import { Select } from '../common/Select';

// Types
type BioType = 'CRL' | 'BPD' | 'HC' | 'AC' | 'FL';
type Trimester = 'T1' | 'T2' | 'T3';
type PregnancyStatus =
  | 'Active'
  | 'Delivered'
  | 'Miscarried'
  | 'Terminated'
  | 'Unknown';

type EducationLevel = 'without' | 'primary' | 'secondary' | 'university';

interface PatientFormData {
  patient_name: string;
  patient_middle_name: string;
  patient_last_name: string;
  email?: string;
  phone_number?: string;
  birth_date?: string;
  address?: string;
  policy_number?: string;
  insurance_provider?: string;
  emergency_details?: string;
  cin?: string;
  education_level?: EducationLevel;
  occupation?: string;
  consanguinity?: boolean;
}

interface PregnancyFormData {
  lmp: string;
  edd: string;
  gaWeeks: string;
  gaDays: string;
  crl: string;
  bpd: string;
  hc: string;
  ac: string;
  fl: string;
  status: PregnancyStatus;
  trimester: Trimester;
}

export interface CombinedFormData {
  patient: PatientFormData;
  pregnancy: PregnancyFormData;
}

interface AddPatientAndPregnancyFormProps {
  onFormDataChange?: (data: CombinedFormData) => void;
  onValidationChange?: (isValid: boolean) => void;
  initialPatientData?: Partial<PatientFormData>;
  initialPregnancyData?: Partial<PregnancyFormData>;
}

// Biometry calculation constants
const BIO_LIMITS: Record<BioType, { min: number; max: number; unit: string }> =
  {
    CRL: { min: 5, max: 95, unit: 'mm' },
    BPD: { min: 2.0, max: 12.0, unit: 'cm' },
    HC: { min: 10.0, max: 40.0, unit: 'cm' },
    AC: { min: 10.0, max: 45.0, unit: 'cm' },
    FL: { min: 1.0, max: 9.0, unit: 'cm' },
  };

// Biometry calculation function
const calculateGA = (
  type: BioType,
  valueStr: string
): {
  totalDays: number;
  weeks: number;
  days: number;
  error?: string;
} | null => {
  const val = parseFloat(valueStr);
  const limit = BIO_LIMITS[type];
  if (!val || isNaN(val) || val < limit.min || val > limit.max)
    return { totalDays: 0, weeks: 0, days: 0, error: 'Invalid value' };

  let gaWeeks = 0;

  if (type === 'BPD') {
    gaWeeks = 9.54 + 1.482 * val + 0.1676 * (val * val);
  } else if (type === 'HC') {
    gaWeeks = 8.96 + 0.54 * val + 0.0003 * Math.pow(val, 3);
  } else if (type === 'AC') {
    gaWeeks = 8.14 + 0.753 * val + 0.0036 * (val * val);
  } else if (type === 'FL') {
    gaWeeks = 10.35 + 2.46 * val + 0.17 * (val * val);
  } else if (type === 'CRL') {
    const val_cm = val / 10.0;
    const exponent =
      1.684969 +
      0.315646 * val_cm -
      0.049306 * Math.pow(val_cm, 2) +
      0.004057 * Math.pow(val_cm, 3) -
      0.000120456 * Math.pow(val_cm, 4);
    gaWeeks = Math.exp(exponent);
  }

  const totalDays = gaWeeks * 7;
  const w = Math.floor(totalDays / 7);
  const d = Math.floor(totalDays % 7);

  if (w < 4 || w > 44)
    return { totalDays: 0, weeks: 0, days: 0, error: 'Out of range' };

  return { totalDays, weeks: w, days: d };
};

const formatGATag = (type: BioType, valueStr: string): string | null => {
  const result = calculateGA(type, valueStr);
  if (!result || result.error) return null;
  return `${result.weeks}w ${result.days}d`;
};

// Helper component for patient form fields
const FormField = ({
  id,
  label,
  error,
  children,
  required = false,
  t,
}: {
  id: string;
  label: string;
  error?: string;
  children: React.ReactNode;
  required?: boolean;
  t?: any;
}) => (
  <div>
    <Label htmlFor={id}>
      {label}{' '}
      {required && (
        <span className="text-red-500">{t?.('required') || '*'}</span>
      )}
    </Label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

// Biometry input component
interface BiometryInputProps {
  type: BioType;
  value: string;
  onChange: (field: string, value: any) => void;
  disabled?: boolean;
}

const BiometryInput: React.FC<BiometryInputProps> = ({
  type,
  value,
  onChange,
  disabled = false,
}) => {
  const gaTag = formatGATag(type, value);
  const limit = BIO_LIMITS[type];
  const isError =
    value && (parseFloat(value) < limit.min || parseFloat(value) > limit.max);

  return (
    <div>
      <label className="text-xs text-slate-600 dark:text-slate-400 font-medium mb-1 block">
        {type}{' '}
        <span className="text-[10px] text-slate-400">({limit.unit})</span>
      </label>
      <div className="relative">
        <input
          type="number"
          step="0.1"
          id={type.toLowerCase()}
          placeholder={`${limit.min}-${limit.max}`}
          value={value}
          onChange={(e) => onChange(type.toLowerCase(), e.target.value)}
          disabled={disabled}
          className={`w-full px-2 py-1.5 pr-16 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 ${
            isError
              ? 'border-red-500 text-red-600 bg-red-50 dark:bg-red-900/20'
              : ''
          }`}
        />
        {gaTag && !isError && (
          <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] font-semibold bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded pointer-events-none">
            {gaTag}
          </span>
        )}
      </div>
    </div>
  );
};

// Main component
export const AddPatientAndPregnancyForm = forwardRef<
  HTMLFormElement,
  AddPatientAndPregnancyFormProps
>(
  (
    {
      onFormDataChange,
      onValidationChange,
      initialPatientData,
      initialPregnancyData,
    },
    ref
  ) => {
    const { t } = useTranslation('Patient');

    // Patient state
    const [patientData, setPatientData] = useState<PatientFormData>({
      patient_name: initialPatientData?.patient_name || '',
      patient_middle_name: initialPatientData?.patient_middle_name || '',
      patient_last_name: initialPatientData?.patient_last_name || '',
      email: initialPatientData?.email || '',
      // phone_number: initialPatientData?.phone_number || '',
      birth_date: initialPatientData?.birth_date || '',
      address: initialPatientData?.address || '',
      policy_number: initialPatientData?.policy_number || '',
      insurance_provider: initialPatientData?.insurance_provider || '',
      emergency_details: initialPatientData?.emergency_details || '',
      cin: initialPatientData?.cin || '',
      education_level: initialPatientData?.education_level || undefined,
      occupation: initialPatientData?.occupation || '',
      consanguinity: initialPatientData?.consanguinity,
    });

    // Compute initial GA from LMP (or EDD) when not explicitly provided
    const _initLmp =
      initialPregnancyData?.lmp ||
      (initialPregnancyData?.edd
        ? dayjs(initialPregnancyData.edd).subtract(280, 'day').format('YYYY-MM-DD')
        : '');
    let _initGaWeeks = initialPregnancyData?.gaWeeks || '';
    let _initGaDays = initialPregnancyData?.gaDays || '';
    if ((!_initGaWeeks || _initGaWeeks === '0') && _initLmp) {
      const _diff = dayjs().diff(dayjs(_initLmp), 'day');
      if (_diff >= 0) {
        _initGaWeeks = Math.floor(_diff / 7).toString();
        _initGaDays = (_diff % 7).toString();
      }
    }
    const _initTrimester: Trimester = (() => {
      const w = parseInt(_initGaWeeks || '0');
      return w >= 28 ? 'T3' : w >= 14 ? 'T2' : 'T1';
    })();

    // Pregnancy state
    const [pregnancyData, setPregnancyData] = useState<PregnancyFormData>({
      lmp: _initLmp,
      edd: initialPregnancyData?.edd || '',
      gaWeeks: _initGaWeeks,
      gaDays: _initGaDays,
      crl: initialPregnancyData?.crl || '',
      bpd: initialPregnancyData?.bpd || '',
      hc: initialPregnancyData?.hc || '',
      ac: initialPregnancyData?.ac || '',
      fl: initialPregnancyData?.fl || '',
      status: initialPregnancyData?.status || 'Active',
      trimester: initialPregnancyData?.trimester || _initTrimester,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    // Calculate biometry state
    const hasCRL = !!pregnancyData.crl && parseFloat(pregnancyData.crl) > 0;
    const hasOthers =
      (!!pregnancyData.bpd && parseFloat(pregnancyData.bpd) > 0) ||
      (!!pregnancyData.hc && parseFloat(pregnancyData.hc) > 0) ||
      (!!pregnancyData.ac && parseFloat(pregnancyData.ac) > 0) ||
      (!!pregnancyData.fl && parseFloat(pregnancyData.fl) > 0);

    const disableCRL = hasOthers;
    const disableOthers = hasCRL;

    // Notify parent of form data changes
    useEffect(() => {
      if (onFormDataChange) {
        onFormDataChange({
          patient: patientData,
          pregnancy: pregnancyData,
        });
      }
    }, [patientData, pregnancyData, onFormDataChange]);

    // Validate form
    const validateForm = (): boolean => {
      const newErrors: Record<string, string> = {};

      // Patient validation
      // if (!patientData.patient_name?.trim())
      //   newErrors.patient_name = t('firstNameRequired');
      // if (!patientData.patient_last_name?.trim())
      //   newErrors.patient_last_name = t('lastNameRequired');

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (
        patientData.email?.trim() &&
        !emailRegex.test(patientData.email.trim())
      )
        newErrors.email = t('validEmailRequired');

      // if (patientData.phone_number) {
      //   try {
      //     const phoneNumber = parsePhoneNumberFromString(
      //       patientData.phone_number
      //     );
      //     if (!phoneNumber || !phoneNumber.isValid())
      //       newErrors.phone_number = t('invalidPhoneNumber');
      //   } catch {
      //     newErrors.phone_number = t('invalidPhoneNumber');
      //   }
      // }

      if (
        patientData.birth_date &&
        new Date(patientData.birth_date) > new Date()
      )
        newErrors.birth_date = t('futureDateError');

      setErrors(newErrors);
      const isValid = Object.keys(newErrors).length === 0;

      if (onValidationChange) {
        onValidationChange(isValid);
      }

      return isValid;
    };

    // Validate on data change
    useEffect(() => {
      validateForm();
    }, [patientData, pregnancyData]);

    // Patient handlers
    const handlePatientChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      const { name, value } = e.target;
      setPatientData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handlePhoneChange = (phone: string) => {
      setPatientData((prev) => ({ ...prev, phone_number: phone }));
      if (errors.phone_number)
        setErrors((prev) => ({ ...prev, phone_number: '' }));
    };

    // Pregnancy handlers
    const handlePregnancyChange = (field: string, value: any) => {
      setPregnancyData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    const handleBiometryChange = (type: BioType, value: string) => {
      setPregnancyData((prev) => {
        const newForm = { ...prev };

        if (type === 'CRL') newForm.crl = value;
        else if (type === 'BPD') newForm.bpd = value;
        else if (type === 'HC') newForm.hc = value;
        else if (type === 'AC') newForm.ac = value;
        else if (type === 'FL') newForm.fl = value;

        const allBiometry = [newForm.crl, newForm.bpd, newForm.hc, newForm.ac, newForm.fl];
        if (!allBiometry.some((v) => v !== '' && v !== null)) {
          return { ...newForm, gaWeeks: '', gaDays: '', lmp: '', edd: '' };
        }

        let calculatedTotalDays = 0;

        if (type === 'CRL' && value) {
          const res = calculateGA('CRL', value);
          if (res && !res.error) calculatedTotalDays = res.totalDays;
        } else {
          const otherFields: ('BPD' | 'HC' | 'AC' | 'FL')[] = ['BPD', 'HC', 'AC', 'FL'];
          let sumDays = 0;
          let count = 0;
          otherFields.forEach((field) => {
            const val = field === type ? value : (newForm as any)[field.toLowerCase()];
            if (val) {
              const res = calculateGA(field, val);
              if (res && !res.error) { sumDays += res.totalDays; count++; }
            }
          });
          if (count > 0) calculatedTotalDays = sumDays / count;
        }

        if (calculatedTotalDays > 0) {
          const w = Math.floor(calculatedTotalDays / 7);
          const d = Math.floor(calculatedTotalDays % 7);
          newForm.gaWeeks = w.toString();
          newForm.gaDays = d.toString();
          const calculatedLmp = dayjs().subtract(Math.round(calculatedTotalDays), 'day');
          newForm.lmp = calculatedLmp.format('YYYY-MM-DD');
          newForm.edd = calculatedLmp.add(280, 'day').format('YYYY-MM-DD');
        }

        return newForm;
      });
    };

    // LMP → EDD + GA
    useEffect(() => {
      if (!pregnancyData.lmp) return;
      const lmp = dayjs(pregnancyData.lmp);
      const newEdd = lmp.add(280, 'day');
      const diffDays = dayjs().diff(lmp, 'day');
      const w = Math.floor(diffDays / 7);
      const d = diffDays % 7;
      if (!dayjs(pregnancyData.edd).isSame(newEdd, 'day')) {
        setPregnancyData((prev) => ({
          ...prev,
          edd: newEdd.format('YYYY-MM-DD'),
          gaWeeks: w >= 0 ? w.toString() : '0',
          gaDays: d >= 0 ? d.toString() : '0',
        }));
      }
    }, [pregnancyData.lmp]);

    // EDD → LMP (back-calculate)
    useEffect(() => {
      if (!pregnancyData.edd) return;
      const expectedLmp = dayjs(pregnancyData.edd).subtract(280, 'day');
      if (!dayjs(pregnancyData.lmp).isSame(expectedLmp, 'day')) {
        setPregnancyData((prev) => ({ ...prev, lmp: expectedLmp.format('YYYY-MM-DD') }));
      }
    }, [pregnancyData.edd]);

    // GA → LMP (user typed in weeks/days directly)
    useEffect(() => {
      const w = parseInt(pregnancyData.gaWeeks || '0');
      const d = parseInt(pregnancyData.gaDays || '0');
      const totalDays = w * 7 + d;
      if (totalDays <= 0) return;
      const calculatedLmp = dayjs().subtract(totalDays, 'day');
      if (!dayjs(pregnancyData.lmp).isSame(calculatedLmp, 'day')) {
        setPregnancyData((prev) => ({ ...prev, lmp: calculatedLmp.format('YYYY-MM-DD') }));
      }
    }, [pregnancyData.gaWeeks, pregnancyData.gaDays]);

    // Trimester from GA weeks
    useEffect(() => {
      const w = parseInt(pregnancyData.gaWeeks || '0');
      if (w > 0) {
        const newTrimester: Trimester = w >= 28 ? 'T3' : w >= 14 ? 'T2' : 'T1';
        if (newTrimester !== pregnancyData.trimester) {
          setPregnancyData((prev) => ({ ...prev, trimester: newTrimester }));
        }
      }
    }, [pregnancyData.gaWeeks]);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      validateForm();
    };

    return (
      <form
        ref={ref}
        onSubmit={handleSubmit}
        className="h-full overflow-y-auto px-1"
      >
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* PATIENT SECTION */}
          <div className="space-y-4">
            <fieldset className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  id="patient_name"
                  label={t('firstName')}
                  error={errors.patient_name}
                  required
                  t={t}
                >
                  <Input
                    id="patient_name"
                    name="patient_name"
                    placeholder={t('enterFirstName')}
                    value={patientData.patient_name}
                    onChange={handlePatientChange}
                  />
                </FormField>

                <FormField
                  id="patient_last_name"
                  label={t('lastName')}
                  error={errors.patient_last_name}
                  required
                  t={t}
                >
                  <Input
                    id="patient_last_name"
                    name="patient_last_name"
                    placeholder={t('enterLastName')}
                    value={patientData.patient_last_name}
                    onChange={handlePatientChange}
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  id="patient_middle_name"
                  label={t('middleName')}
                  error={errors.patient_middle_name}
                  t={t}
                >
                  <Input
                    id="patient_middle_name"
                    name="patient_middle_name"
                    placeholder={t('enterMiddleName')}
                    value={patientData.patient_middle_name}
                    onChange={handlePatientChange}
                  />
                </FormField>
                <FormField
                  id="email"
                  label={t('emailAddress')}
                  error={errors.email}
                  t={t}
                >
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder={t('emailPlaceholder')}
                    value={patientData.email}
                    onChange={handlePatientChange}
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  id="birth_date"
                  label={t('dateOfBirth')}
                  error={errors.birth_date}
                  t={t}
                >
                  <DateInput
                    id="birth_date"
                    name="birth_date"
                    value={patientData.birth_date}
                    onChange={handlePatientChange}
                    max={dayjs().format('YYYY-MM-DD')}
                  />
                </FormField>

                <FormField
                  id="phone_number"
                  label={t('phoneNumber')}
                  error={errors.phone_number}
                  t={t}
                >
                  <PhoneInput
                    defaultCountry="ma"
                    countrySelectorStyleProps={{
                      buttonStyle: {
                        backgroundColor: 'transparent',
                        borderColor: 'rgb(203 213 225)',
                        color: 'inherit',
                      },
                    }}
                    value={patientData.phone_number}
                    onChange={handlePhoneChange}
                    inputClassName={` w-full !bg-transparent dark:!bg-gray-800 !text-title-page-color-300 ${errors.phone_number ? 'rip-input--error' : ''}`}
                  />
                </FormField>
              </div>

              <FormField
                id="address"
                label={t('address')}
                error={errors.address}
                t={t}
              >
                <Input
                  id="address"
                  name="address"
                  placeholder={t('addressPlaceholder')}
                  value={patientData.address}
                  onChange={handlePatientChange}
                />
              </FormField>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  id="cin"
                  label={t('cin')}
                  error={errors.cin}
                  t={t}
                >
                  <Input
                    id="cin"
                    name="cin"
                    placeholder={t('cinPlaceholder')}
                    value={patientData.cin}
                    onChange={handlePatientChange}
                  />
                </FormField>

                <FormField
                  id="occupation"
                  label={t('occupation')}
                  error={errors.occupation}
                  t={t}
                >
                  <Input
                    id="occupation"
                    name="occupation"
                    placeholder={t('occupationPlaceholder')}
                    value={patientData.occupation}
                    onChange={handlePatientChange}
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  id="education_level"
                  label={t('educationLevel')}
                  error={errors.education_level}
                  t={t}
                >
                  <Select
                    id="education_level"
                    name="education_level"
                    value={patientData.education_level || ''}
                    onChange={(e) => setPatientData((prev) => ({ ...prev, education_level: (e.target.value || undefined) as EducationLevel | undefined }))}
                    className="flex h-10 w-full rounded-lg border border-slate-300 dark:border-gray-600 bg-transparent dark:bg-gray-800 px-3 py-2 text-sm ring-offset-white dark:ring-offset-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 text-title-page-color-300 dark:text-gray-300"
                    placeholder={t('selectEducationLevel')}
                    options={[
                      { value: 'without', label: t('educationWithout') },
                      { value: 'primary', label: t('educationPrimary') },
                      { value: 'secondary', label: t('educationSecondary') },
                      { value: 'university', label: t('educationUniversity') },
                    ]}
                  />
                </FormField>

                <FormField
                  id="consanguinity"
                  label={t('consanguinity')}
                  error={errors.consanguinity}
                  t={t}
                >
                  <Select
                    id="consanguinity"
                    name="consanguinity"
                    value={patientData.consanguinity === true ? 'true' : patientData.consanguinity === false ? 'false' : ''}
                    onChange={(e) => { const val = e.target.value; setPatientData((prev) => ({ ...prev, consanguinity: val === '' ? undefined : val === 'true' })); }}
                    className="flex h-10 w-full rounded-lg border border-slate-300 dark:border-gray-600 bg-transparent dark:bg-gray-800 px-3 py-2 text-sm ring-offset-white dark:ring-offset-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 text-title-page-color-300 dark:text-gray-300"
                    placeholder={t('selectConsanguinity')}
                    options={[
                      { value: 'true', label: t('yes') },
                      { value: 'false', label: t('no') },
                    ]}
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  id="policy_number"
                  label={t('policyNumber')}
                  error={errors.policy_number}
                  t={t}
                >
                  <Input
                    id="policy_number"
                    name="policy_number"
                    placeholder={t('policyNumberPlaceholder')}
                    value={patientData.policy_number}
                    onChange={handlePatientChange}
                  />
                </FormField>

                <FormField
                  id="insurance_provider"
                  label={t('insuranceProvider')}
                  error={errors.insurance_provider}
                  t={t}
                >
                  <Input
                    id="insurance_provider"
                    name="insurance_provider"
                    placeholder={t('providerNamePlaceholder')}
                    value={patientData.insurance_provider}
                    onChange={handlePatientChange}
                  />
                </FormField>
              </div>

              <FormField
                id="emergency_details"
                label={t('emergencyDetails')}
                error={errors.emergency_details}
                t={t}
              >
                <textarea
                  id="emergency_details"
                  name="emergency_details"
                  rows={3}
                  className="flex w-full rounded-lg border border-slate-300 dark:border-gray-600 bg-transparent dark:bg-gray-800 px-3 py-2 text-sm ring-offset-white dark:ring-offset-gray-900 placeholder:text-title-page-color-300 dark:placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 text-title-page-color-300 dark:text-gray-300"
                  placeholder={t('emergencyContactPlaceholder')}
                  value={patientData.emergency_details}
                  onChange={handlePatientChange}
                />
              </FormField>
            </fieldset>
          </div>

          {/* PREGNANCY SECTION */}
          <div className="space-y-4 xl:border-l-2 xl:border-t-0 border-t-2 xl:pl-6 pt-4 xl:pt-0 border-slate-300 dark:border-gray-600">
            {/* Trimester */}
            <div className="flex gap-2 bg-pink-50 dark:bg-pink-900/20 p-2 rounded border border-pink-200 dark:border-pink-800 items-center justify-between w-fit">
              <span className="text-xs text-slate-600 dark:text-slate-400 font-semibold">Trimester:</span>
              <span className="text-xs font-bold bg-pink-600 text-white px-2 py-0.5 rounded-full">{pregnancyData.trimester}</span>
            </div>

            {/* LMP and EDD */}
            <div className="grid grid-cols-2 gap-3">
              <FormFieldCommon label="LMP" icon={FiCalendar}>
                <DateInput
                  id="lmp"
                  name="lmp"
                  value={pregnancyData.lmp}
                  onChange={(e) => handlePregnancyChange('lmp', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                />
              </FormFieldCommon>
              <FormFieldCommon label="EDD" icon={FiCalendar}>
                <DateInput
                  id="edd"
                  name="edd"
                  value={pregnancyData.edd}
                  onChange={(e) => handlePregnancyChange('edd', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                />
              </FormFieldCommon>
            </div>

            {/* Gestational Age inputs — local only, control LMP, not sent to backend */}
            <div className="grid grid-cols-2 gap-3">
              <FormFieldCommon label="Weeks" htmlFor="gaWeeks">
                <Input
                  id="gaWeeks"
                  name="gaWeeks"
                  type="number"
                  min="0"
                  max="45"
                  placeholder="0-45"
                  value={pregnancyData.gaWeeks}
                  onChange={(e) => handlePregnancyChange('gaWeeks', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                />
              </FormFieldCommon>
              <FormFieldCommon label="Days" htmlFor="gaDays">
                <Input
                  id="gaDays"
                  name="gaDays"
                  type="number"
                  min="0"
                  max="6"
                  placeholder="0-6"
                  value={pregnancyData.gaDays}
                  onChange={(e) => handlePregnancyChange('gaDays', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                />
              </FormFieldCommon>
            </div>

            {/* Biometry Section */}
            <div className="border-t border-slate-200 dark:border-gray-700 pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-300">
                  <FiActivity
                    className="text-blue-600 dark:text-blue-400"
                    size={14}
                  />
                  Biometry
                </div>
                {disableOthers && (
                  <span className="text-[10px] text-amber-600 flex items-center">
                    <FiLock className="mr-1" /> T1 Mode (CRL)
                  </span>
                )}
                {disableCRL && (
                  <span className="text-[10px] text-blue-600 flex items-center">
                    <FiLock className="mr-1" /> T2/T3 Mode
                  </span>
                )}
              </div>

              {/* First Trimester (CRL) */}
              <div
                className={
                  disableCRL
                    ? 'opacity-50 pointer-events-none grayscale mb-3'
                    : 'mb-3'
                }
              >
                <BiometryInput
                  type="CRL"
                  value={pregnancyData.crl}
                  onChange={(field, value) =>
                    handleBiometryChange('CRL', value)
                  }
                  disabled={disableCRL}
                />
              </div>

              {/* Second/Third Trimester */}
              <div
                className={`grid grid-cols-2 gap-3 ${disableOthers ? 'opacity-50 pointer-events-none grayscale' : ''}`}
              >
                <BiometryInput
                  type="BPD"
                  value={pregnancyData.bpd}
                  onChange={(field, value) =>
                    handleBiometryChange('BPD', value)
                  }
                  disabled={disableOthers}
                />
                <BiometryInput
                  type="HC"
                  value={pregnancyData.hc}
                  onChange={(field, value) => handleBiometryChange('HC', value)}
                  disabled={disableOthers}
                />
                <BiometryInput
                  type="AC"
                  value={pregnancyData.ac}
                  onChange={(field, value) => handleBiometryChange('AC', value)}
                  disabled={disableOthers}
                />
                <BiometryInput
                  type="FL"
                  value={pregnancyData.fl}
                  onChange={(field, value) => handleBiometryChange('FL', value)}
                  disabled={disableOthers}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    );
  }
);

AddPatientAndPregnancyForm.displayName = 'AddPatientAndPregnancyForm';

export default AddPatientAndPregnancyForm;
